import { useEffect, useState, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, MemoryRouter } from "react-router-dom";
import { ThemeProvider, ToastProvider, bindMutationCache, type ThemeName } from "@guestpilot/ui-kit";
import { App } from "./App";
import { ApiProblemError } from "./api";
import { useAuth } from "./auth";
import { configureApi } from "./api";

const THEME_KEY = "gp-console-theme";

/** Theme: the stored choice, else the OS preference (BR-260); storage may be unavailable, so it is only a convenience. */
function initialTheme(): ThemeName {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    /* storage blocked: follow the OS */
  }
  return typeof window.matchMedia === "function" && window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function makeQueryClient(): QueryClient {
  const client = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        refetchOnWindowFocus: false,
        retry: (count, err) => !(err instanceof ApiProblemError && err.status < 500) && count < 2,
      },
    },
  });
  bindMutationCache(client.getMutationCache()); // a button that starts a request is busy until it ends
  return client;
}

function TokenBridge({ children }: { children: ReactNode }) {
  const auth = useAuth();
  configureApi({ getToken: auth.getToken });
  return <>{children}</>;
}

/** Providers in one place: query cache, theme (own toggle, BR-260), toasts, router. */
export function Root({
  router = "browser",
  initialPath,
  client,
}: {
  router?: "browser" | "memory";
  initialPath?: string;
  client?: QueryClient;
}) {
  const [qc] = useState(() => client ?? makeQueryClient());
  const [theme, setTheme] = useState<ThemeName>(initialTheme);
  useEffect(() => {
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      /* ignore */
    }
  }, [theme]);
  const Router = router === "memory" ? MemoryRouter : BrowserRouter;
  const routerProps = router === "memory" ? { initialEntries: [initialPath ?? "/"] } : {};
  return (
    <QueryClientProvider client={qc}>
      <ThemeProvider theme={theme}>
        <ToastProvider closeLabel="Close">
          <TokenBridge>
            <Router {...routerProps}>
              <App theme={theme} onToggleTheme={() => setTheme((t) => (t === "dark" ? "light" : "dark"))} />
            </Router>
          </TokenBridge>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
